/**
 * Context Loader Utility for Soniox Transcription
 *
 * This utility provides functions to load context from various sources
 * to improve transcription accuracy with Soniox Speech-to-Text API.
 *
 * Key Principles:
 * - Soniox allows ~8k tokens for context
 * - Keywords format is cleaner and avoids hallucinations vs paragraph style
 * - Include language hints for mixed-language scenarios (e.g., Hinglish)
 * - Keep context condensed but comprehensive for domain-specific terms
 */

/**
 * Load context from a local file using File API
 * @param {File} file - The file object from an input element
 * @returns {Promise<string>} - The context text
 */
export const loadContextFromFile = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }

    if (file.type !== 'text/plain' && !file.name.endsWith('.txt')) {
      reject(new Error('Only text files (.txt) are supported'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (event) => {
      const text = event.target.result;
      
      // Validate context length (Soniox supports up to 20,000 characters)
      if (text.length > 20000) {
        console.warn('Context text exceeds 20,000 characters and will be truncated');
        resolve(text.substring(0, 20000));
      } else {
        resolve(text);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
};

/**
 * Load context from a URL
 * @param {string} url - The URL to fetch context from
 * @returns {Promise<string>} - The context text
 */
export const loadContextFromUrl = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch context: ${response.status} ${response.statusText}`);
    }
    
    const text = await response.text();
    
    // Validate context length (Soniox supports up to 20,000 characters)
    if (text.length > 20000) {
      console.warn('Context text exceeds 20,000 characters and will be truncated');
      return text.substring(0, 20000);
    }
    
    return text;
  } catch (error) {
    throw new Error(`Failed to load context from URL: ${error.message}`);
  }
};

/**
 * Validate context text based on Soniox best practices
 * @param {string} context - The context text to validate
 * @returns {object} - Validation result with isValid, message, and tokenEstimate
 */
export const validateContext = (context) => {
  if (!context || typeof context !== 'string') {
    return {
      isValid: false,
      message: 'Context must be a non-empty string',
      tokenEstimate: 0
    };
  }

  // Rough token estimation (1 token ≈ 4 characters for English)
  const estimatedTokens = Math.ceil(context.length / 4);

  if (context.length > 20000) {
    return {
      isValid: false,
      message: 'Context exceeds 20,000 character limit',
      tokenEstimate: estimatedTokens
    };
  }

  if (estimatedTokens > 8000) {
    return {
      isValid: false,
      message: `Context estimated at ${estimatedTokens} tokens, exceeds Soniox ~8k token limit. Consider condensing to keywords only.`,
      tokenEstimate: estimatedTokens
    };
  }

  return {
    isValid: true,
    message: `Context is valid (~${estimatedTokens} tokens)`,
    tokenEstimate: estimatedTokens
  };
};

/**
 * Create context from keyword list
 * @param {string[]} keywords - Array of keywords
 * @returns {string} - Formatted context string
 */
export const createContextFromKeywords = (keywords) => {
  if (!Array.isArray(keywords) || keywords.length === 0) {
    return '';
  }
  
  return keywords.join(', ');
};

/**
 * Sample context templates for different domains
 * Optimized for Soniox: keyword format, condensed, domain-specific
 */
export const contextTemplates = {
  // Medical - condensed keyword list
  medical: `Celebrex, Zyrtec, Xanax, Prilosec, Amoxicillin Clavulanate Potassium, Metformin, Lisinopril, Atorvastatin, Hypertension, Diabetes Mellitus, Hyperlipidemia, Echocardiogram, Electrocardiogram, MRI, CT scan, Ultrasound, Endoscopy, Colonoscopy`,

  // Business - key terms only
  business: `Revenue, EBITDA, ROI, KPI, Customer Acquisition Cost, Market Penetration, Quarterly Report, Stakeholder, Board Meeting, Due Diligence, Synergy, Merger, Acquisition`,

  // Technology - essential tech terms
  technology: `API, Machine Learning, Artificial Intelligence, Cloud Computing, Microservices, DevOps, Kubernetes, Docker, CI/CD, REST, GraphQL, Database, Frontend, Backend, Full Stack`,

  // Legal - core legal terminology
  legal: `Plaintiff, Defendant, Jurisdiction, Litigation, Deposition, Discovery, Motion, Subpoena, Affidavit, Testimony, Evidence, Precedent, Contract, Liability, Damages, Settlement`,

  // Industrial/Manufacturing - optimized for technical environments
  industrial: `gearbox, gear bolt loose, gear tooth wear, bearing overheating, bearing spalling, coupling failure, shaft misalignment, shaft crack, roll chip, roll wear, mandrel seizure, feed jam, feed slip, hydraulic pump cavitation, seal leak, hose leak, valve stuck, motor bearing overheating, winding short, vfd trip, earth fault, breaker trip, sensor failure, lubrication pump failure, lube line blockage`,

  // Hinglish Industrial - mixed language for Indian manufacturing
  hinglish_industrial: `bolt loose hai, gear ka daant toot gaya, bearing garam ho raha hai, roll chip ho gaya, mandrel atak gaya, feed jam hua, feed slip ho gaya, tel leak kar raha hai, pump cavitation sound aa raha hai, motor rumbling hai, drive trip ho gaya, hose phat gaya, sensor signal nahi aa raha, breaker baar baar trip kar raha hai`
};

/**
 * Create optimized Soniox configuration based on domain and language requirements
 * @param {string} domain - Domain type (medical, industrial, business, etc.)
 * @param {object} options - Configuration options
 * @returns {object} - Optimized Soniox configuration
 */
export const createOptimizedSonioxConfig = (domain = 'general', options = {}) => {
  const {
    includeHinglish = false,
    enableEndpointDetection = true,
    enableLanguageIdentification = false,
    enableSpeakerDiarization = false,
    maxNonFinalTokensDurationMs = 6000, // Balanced for noisy environments
    customContext = null,
    customLanguageHints = null
  } = options;

  // Base configuration
  const config = {
    model: "stt-rt-preview-v2",
    enable_endpoint_detection: enableEndpointDetection,
    enable_language_identification: enableLanguageIdentification,
    enable_speaker_diarization: enableSpeakerDiarization,
    max_non_final_tokens_duration_ms: maxNonFinalTokensDurationMs,
    chunk_length_ms: 250 // Optimized for performance
  };

  // Language hints based on domain and requirements
  if (customLanguageHints) {
    config.language_hints = customLanguageHints;
  } else if (includeHinglish || domain.includes('hinglish')) {
    config.language_hints = ["en", "hi"]; // Essential for mixed-language scenarios
  } else {
    config.language_hints = ["en"];
  }

  // Context based on domain
  if (customContext) {
    config.context = customContext;
  } else if (contextTemplates[domain]) {
    config.context = contextTemplates[domain];

    // For industrial domains, combine with Hinglish if requested
    if (domain === 'industrial' && includeHinglish && contextTemplates.hinglish_industrial) {
      config.context = `${contextTemplates[domain]}, ${contextTemplates.hinglish_industrial}`;
    }
  }

  return config;
};

/**
 * Create industrial context optimized for manufacturing environments
 * Based on your Pilger Machine example - condensed technical lexicon + Hinglish phrases
 * @param {boolean} includeHinglish - Include mixed-language phrases
 * @returns {string} - Optimized industrial context
 */
export const createIndustrialContext = (includeHinglish = true) => {
  const technicalTerms = [
    // Mechanical failures
    "gearbox, gear bolt loose, gear tooth wear, gear tooth breakage, shaft misalignment, shaft crack",
    "bearing overheating, bearing spalling, coupling failure, key shear, keyway fretting",
    "roll chip, roll wear, roll misalignment, mandrel seizure, mandrel wear, guide shoe wear",
    "feed jam, feed slip, roll bearing failure",

    // Hydraulic system
    "hydraulic pump cavitation, pump failure, seal leak, hose leak, hose burst, valve stuck, valve leakage",
    "cylinder seal leak, cylinder pin break, bent rod, accumulator low charge, filter clog, oil contamination, heat exchanger fouling",

    // Electrical system
    "motor bearing overheating, winding short, shaft misalignment, cooling fan failure, vfd trip, earth fault",
    "loose terminal, breaker trip, sensor contamination, sensor failure, cable damage, loose connector",

    // Lubrication system
    "lubrication pump failure, lube line blockage, lube filter clog, hose leak, heat exchanger fouling"
  ];

  const hinglishPhrases = [
    "bolt loose hai, gear ka daant toot gaya, bearing garam ho raha hai, roll chip ho gaya, mandrel atak gaya",
    "feed jam hua, feed slip ho gaya, tel leak kar raha hai, pump cavitation sound aa raha hai",
    "motor rumbling hai, drive trip ho gaya, hose phat gaya, sensor signal nahi aa raha, breaker baar baar trip kar raha hai"
  ];

  let context = technicalTerms.join(', ');

  if (includeHinglish) {
    context += ', ' + hinglishPhrases.join(', ');
  }

  return context;
};
